import { getToken, clearAuth, loginRedirect } from './auth.js';

const BASE = '/api/v1';

async function request(path, opts = {}) {
  const token = getToken();
  const headers = new Headers(opts.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (opts.body && !(opts.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(BASE + path, { ...opts, headers });
  const text = await res.text();
  const data = text ? safeJSON(text) : null;
  if (res.status === 401) {
    // Preserve the JWT on transient nbf-not-yet (clock skew); the user can
    // retry the action manually. For a real bad/expired token, wipe and
    // bounce to login.
    const reason = String(data?.error || '');
    if (!/not valid yet/i.test(reason)) {
      clearAuth();
      loginRedirect();
    }
    throw new Error(reason || 'unauthenticated');
  }
  if (res.status === 403) {
    // Whitelist failure -- don't redirect; just bubble up so the caller
    // (typically App.svelte's bootstrap) can render the no-access page.
    throw new Error('no_access');
  }
  if (!res.ok) {
    // data?.error covers our Go handlers (they always emit {error: msg}).
    // res.statusText is empty on HTTP/2, and a gateway/ingress error (Knative
    // 502/503/504) returns a non-JSON body so data is null -- without the
    // status fallback the thrown message is "" and the SPA shows an empty
    // error toast. Always surface something actionable.
    throw new Error(data?.error || res.statusText || `request failed (HTTP ${res.status})`);
  }
  return data;
}

function safeJSON(text) {
  try { return JSON.parse(text); } catch { return null; }
}

export const api = {
  // Buckets
  listBuckets: () => request('/buckets'),
  createBucket: (body) => request('/buckets', { method: 'POST', body: JSON.stringify(body) }),
  getBucket: (id) => request(`/buckets/${id}`),
  updateBucket: (id, body) => request(`/buckets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteBucket: (id) => request(`/buckets/${id}`, { method: 'DELETE' }),

  // Captures
  captureMeta: () => request('/captures/_meta'),
  listCaptures: (filter = {}, { signal } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    }
    const q = qs.toString();
    return request('/captures' + (q ? `?${q}` : ''), { signal });
  },
  getCapture: (id) => request(`/captures/${id}`),
  deleteCapture: (id) => request(`/captures/${id}`, { method: 'DELETE' }),
  bulkDeleteCaptures: (bucketId) => request(`/captures?bucket_id=${encodeURIComponent(bucketId)}`, { method: 'DELETE' }),
  // Delete a specific set of captures (Captures table checkbox selection).
  deleteCapturesByIds: (ids) => request(`/captures?ids=${ids.map(encodeURIComponent).join(',')}`, { method: 'DELETE' }),

  // Audit
  listAudit: (resource = '', limit = 100) => {
    const qs = new URLSearchParams();
    if (resource) qs.set('resource', resource);
    if (limit) qs.set('limit', String(limit));
    return request('/audit?' + qs.toString());
  },

  // Winston logs (shared "logs" collection from telebroad-integrations).
  // filter keys: from, to, before_id, levels, hostnames, applications,
  // sections, versions, log_types (CSV), customer_id, people_id (int),
  // phone_id, call_id, transaction_id, path (str), skip, limit.
  listLogs: (filter = {}, { signal } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    const q = qs.toString();
    return request('/logs' + (q ? `?${q}` : ''), { signal });
  },
  // logDistinct narrows the dropdown values by the same filter set the SPA
  // would send to /logs, minus the field being queried (server-side). The
  // filter arg has the same shape as listLogs's filter.
  // Enswitch directory pickers (customer/people/phone) -- powers the
  // human-readable dropdowns on the Logs filter. Returns 503 when MariaDB
  // isn't configured (e.g. local dev outside the Telebroad VPN).
  listCustomers:    ({ signal } = {}) => request('/directory/customers', { signal }),
  listPeople:       (customerId = 0, { signal } = {}) => request('/directory/people' + (customerId ? '?customer_id=' + encodeURIComponent(customerId) : ''), { signal }),
  listPhonesByUser: (peopleId, { signal } = {}) => request(`/directory/people/${peopleId}/phones`, { signal }),
  // SIP lines for the Phone ID picker. Either scope it to a customer, or pass
  // a query -- searching every customer without one is refused server-side.
  listPhones:       ({ customerId = 0, q = '', limit = 0 } = {}, { signal } = {}) => {
    const p = new URLSearchParams();
    if (customerId) p.set('customer_id', customerId);
    if (q) p.set('q', q);
    if (limit) p.set('limit', limit);
    return request('/directory/phones?' + p.toString(), { signal });
  },

  // ap-server's outbound-email send log (read-only). filter keys: recipient,
  // status (sent|failed), from, to (datetime-local or RFC3339), skip, limit.
  // Returns { logs, total, total_exact }.
  listEmailLogs: (filter = {}, { signal } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, String(v));
    }
    const q = qs.toString();
    return request('/email-logs' + (q ? `?${q}` : ''), { signal });
  },

  logDistinct: (field, filter = {}, { signal } = {}) => {
    const qs = new URLSearchParams();
    qs.set('field', field);
    for (const [k, v] of Object.entries(filter)) {
      if (v === undefined || v === null || v === '') continue;
      qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    return request('/logs/distinct?' + qs.toString(), { signal });
  },

  // Status checker (monitored URLs).
  listStatusTargets:    () => request('/status/targets'),
  createStatusTarget:   (body) => request('/status/targets', { method: 'POST', body: JSON.stringify(body) }),
  updateStatusTarget:   (id, body) => request(`/status/targets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteStatusTarget:   (id) => request(`/status/targets/${id}`, { method: 'DELETE' }),
  statusTargetHistory:  (id, limit = 60) => request(`/status/targets/${id}/history?limit=${limit}`),

  // Settings (admin-only). The alerts doc is a singleton; PUT replaces it.
  getAlertSettings:  () => request('/settings/alerts'),
  putAlertSettings:  (body) => request('/settings/alerts', { method: 'PUT', body: JSON.stringify(body) }),
  testAlertSettings: (body) => request('/settings/alerts/test', { method: 'POST', body: JSON.stringify(body) }),

  // Email delivery probe (admin-only). Singleton settings doc; PUT replaces it.
  // testProbeImap verifies the IMAP connection with the *posted* settings.
  // listEmailProbes returns the recent send/receipt log (newest first).
  getProbeSettings:  () => request('/settings/probe'),
  putProbeSettings:  (body) => request('/settings/probe', { method: 'PUT', body: JSON.stringify(body) }),
  testProbeImap:     (body) => request('/settings/probe/test-imap', { method: 'POST', body: JSON.stringify(body) }),
  listEmailProbes:   (limit = 100) => request('/email-probes?limit=' + encodeURIComponent(limit)),

  // Users (whitelist + roles). Admin-only writes return 403 if the caller isn't admin.
  listUsers: () => request('/users'),
  // Look up a username in enswitch.people for autofill. Throws 'no Telebroad
  // user found with that username' on miss; throws 'directory not available'
  // when MariaDB isn't reachable.
  lookupDirectoryUser: (username) =>
    request('/users/lookup?username=' + encodeURIComponent(username)),
  grantUser: (body) => request('/users', { method: 'POST', body: JSON.stringify(body) }), // body: { username, first_name?, last_name?, role? }
  // For placeholder users (user_id <= 0) we route by username instead of by
  // numeric id so the lookup is precision-safe across JSON.
  revokeUser: (u) => u.user_id > 0
    ? request(`/users/${u.user_id}`, { method: 'DELETE' })
    : request(`/users/by-username/${encodeURIComponent(u.username)}`, { method: 'DELETE' }),
  setUserRole: (u, role) => u.user_id > 0
    ? request(`/users/${u.user_id}`, { method: 'PATCH', body: JSON.stringify({ role }) })
    : request(`/users/by-username/${encodeURIComponent(u.username)}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  // Email a grantee a sign-in link. Always routes by username (unique on
  // every grant; avoids a by-id route that would clash with the
  // by-username PATCH/DELETE routes in the server mux).
  inviteUser: (u) => request(`/users/by-username/${encodeURIComponent(u.username)}/invite`, { method: 'POST' }),

  // SFTP tester. Buckets hold credentials (username + password + authorized
  // keys); captures are upload + auth events. Reads need access; mutations
  // need admin.
  sftpMeta: () => request('/sftp/_meta'),
  listSftpBuckets: () => request('/sftp/buckets'),
  getSftpBucket: (id) => request(`/sftp/buckets/${id}`),
  createSftpBucket: (body) => request('/sftp/buckets', { method: 'POST', body: JSON.stringify(body) }),
  updateSftpBucket: (id, body) => request(`/sftp/buckets/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  deleteSftpBucket: (id) => request(`/sftp/buckets/${id}`, { method: 'DELETE' }),
  // Returns { private_key, public_key, type, fingerprint }. The private key is
  // shown once and never stored server-side.
  generateSftpKey: (id, username, type, bits, comment) =>
    request(`/sftp/buckets/${id}/credentials/${encodeURIComponent(username)}/generate-key`, { method: 'POST', body: JSON.stringify({ type, bits, comment }) }),
  // Stateless keypair generation (no bucket needed) -- used while creating or
  // editing a bucket. Caller appends the returned public key to the credential.
  // Returns { private_key, public_key, type, fingerprint }; private key shown once.
  generateSftpKeypair: (type, bits, comment) =>
    request('/sftp/generate-key', { method: 'POST', body: JSON.stringify({ type, bits, comment }) }),
  listSftpCaptures: (filter = {}, { signal } = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filter)) {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    }
    const q = qs.toString();
    return request('/sftp/captures' + (q ? `?${q}` : ''), { signal });
  },
  getSftpCapture: (id) => request(`/sftp/captures/${id}`),
  deleteSftpCapture: (id) => request(`/sftp/captures/${id}`, { method: 'DELETE' }),
  bulkDeleteSftpCaptures: (bucketId) => request(`/sftp/captures?bucket_id=${encodeURIComponent(bucketId)}`, { method: 'DELETE' }),
  // Delete a specific set of captures (Activity tab checkbox selection).
  deleteSftpCapturesByIds: (ids) => request(`/sftp/captures?ids=${ids.map(encodeURIComponent).join(',')}`, { method: 'DELETE' }),
  // SFTP firewall: blocked source IPs (manual + auto after failed attempts).
  listSftpFirewall: () => request('/sftp/firewall'),
  blockSftpIp: (ip, reason) => request('/sftp/firewall', { method: 'POST', body: JSON.stringify({ ip, reason }) }),
  unblockSftpIp: (ip) => request(`/sftp/firewall/${encodeURIComponent(ip)}`, { method: 'DELETE' }),

  // Smokescreen analytics — backend proxies PromQL to the telebroad
  // Prometheus instances (ave + webair). The SPA passes raw PromQL
  // expressions; the response is the standard Prometheus JSON shape
  // (data.result is the array of series for chart rendering).
  smokescreenInfo: ({ signal } = {}) => request('/metrics/smokescreen/info', { signal }),
  smokescreenQuery: (expr, opts = {}) => {
    const q = new URLSearchParams({ expr });
    if (opts.time) q.set('time', String(opts.time));
    return request('/metrics/smokescreen/query?' + q.toString(), { signal: opts.signal });
  },
  smokescreenRange: (expr, opts = {}) => {
    const q = new URLSearchParams({ expr });
    if (opts.from) q.set('from', String(opts.from));
    if (opts.to)   q.set('to',   String(opts.to));
    if (opts.step) q.set('step', String(opts.step));
    return request('/metrics/smokescreen/query_range?' + q.toString(), { signal: opts.signal });
  },
};
